import React, { useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { getCurrentReservationPath } from "@/components/pages/reservations/utils/getCurrentReservationPath";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

export const TAB_PARAM = "tab";

const TABS = [
  { id: "info", label: "reservation.profile.reservationInfo" },
  { id: "guest", label: "reservation.profile.guestInfo" },
  { id: "payments", label: "reservation.profile.payments" },
  { id: "requests", label: "reservation.profile.requests" },
  { id: "other", label: "reservation.profile.other" },
] as const;

export type ReservationDetailTabId = (typeof TABS)[number]["id"];

interface ReservationDetailTabsProps {
  reservationId: string;
}

export const ReservationDetailTabs: React.FC<ReservationDetailTabsProps> = ({
  reservationId,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { capabilities } = useAccessCapabilities();

  const requestedTabParam =
    (searchParams.get(TAB_PARAM) as ReservationDetailTabId) || "info";
  const tabParam =
    requestedTabParam === "requests" && !capabilities.canUseRequestsSystem
      ? "info"
      : requestedTabParam;

  const reservationPath = useMemo(() => {
    const startPath = getCurrentReservationPath(location.pathname);
    return `${startPath}/${reservationId}`;
  }, [location.pathname, reservationId]);

  const isOnReservationPage = location.pathname.startsWith(reservationPath);
  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (tab) => tab.id !== "requests" || capabilities.canUseRequestsSystem,
      ),
    [capabilities.canUseRequestsSystem],
  );

  return (
    <nav
      className="flex items-center gap-1 px-1 py-[6px] bg-light-slate shadow-sm rounded-[10px]"
      aria-label="Reservation sections"
    >
      {visibleTabs.map((tab) => {
        const to =
          tab.id === "info"
            ? reservationPath
            : `${reservationPath}?${TAB_PARAM}=${tab.id}`;
        const isActive =
          isOnReservationPage &&
          (tab.id === "info"
            ? tabParam === "info" || !searchParams.has(TAB_PARAM)
            : tabParam === tab.id);

        return (
          <Link
            key={tab.id}
            to={to}
            className={cn(
              "flex-1 px-2 py-1 text-center text-xs text-foreground font-medium transition-colors rounded-lg",
              isActive &&
                "bg-light-indigo rounded-lg border border-light-violet shadow-sm",
            )}
          >
            {t(tab.label)}
          </Link>
        );
      })}
    </nav>
  );
};
